import moment from "moment";
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from "react";

import {
  Button,
  ButtonOwnProps,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AttributionIcon from "@mui/icons-material/Attribution";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { visuallyHidden } from "@mui/utils";
import { Box, TableContainer, TablePagination, TableSortLabel } from "@mui/material";

import conf from "../../conf";
import format from "../utils/Role";
import Spinner from "../utils/Spinner";
import { Project } from "../../hooks/proxy/projects";
import EmptyTableRow from "../utils/Empty";
import usePagination from "../../hooks/pagination";
import { useConfgUsers } from "../../hooks/confg/base";
import { useAuth, User } from "../auth/AuthProvider";
import { Offline, Online } from "../utils/Status";
import LinearIndeterminate from "../utils/LinearLoader";
import { EditAssignment, ActionType } from "../users/Radio";
import { useProxyProjectsAssign, useProxyProjectsUnassign } from "../../hooks/proxy/projects";

const UserRow = (props: {
  user: User;
  action: ActionType;
  project: Project;
  handleClick: (e: React.MouseEvent<unknown>, id: string) => void;
  isItemSelected: boolean;
}) => {
  const users = props.project.users;
  const { action, user, isItemSelected, handleClick } = props;
  const { id, email, lastActive } = user;

  const { user: self } = useAuth();
  const organizationId = self.activeOrganization.id;

  const isAssigned = users?.some((id) => id === user.id);
  const isDisabled = (action === "assign" && isAssigned) || (action === "unassign" && !isAssigned);
  const { role, status } = user.organizations.find((org) => org.id === organizationId) || {};

  const Status = (
    <Chip size="small" label={status} color={status === "active" ? "success" : status === "pending" ? "warning" : "error"} />
  );

  const EmailIcon =
    email === self.email ? (
      <AttributionIcon fontSize="small" color="success" />
    ) : user.session.status === "active" ? (
      Online
    ) : (
      Offline
    );

  return (
    <TableRow
      key={id}
      hover={!isDisabled}
      onClick={isDisabled ? () => {} : (e) => handleClick(e, id)}
      tabIndex={-1}
      selected={isItemSelected}
      aria-checked={isItemSelected}
      sx={conf.STYLE.LAST_CHILD_NO_BORDER}
    >
      <TableCell padding="checkbox">
        <Checkbox
          color="primary"
          checked={isItemSelected}
          onClick={(e) => handleClick(e, id)}
          disabled={isDisabled}
          inputProps={{ "aria-labelledby": id }}
        />
      </TableCell>

      <TableCell component="th" scope="row">
        <Chip size="small" label={email} icon={EmailIcon} />
      </TableCell>

      <TableCell align="right">{Status}</TableCell>

      <TableCell align="right">{format(role || "--")}</TableCell>

      <TableCell align="right">{moment(lastActive).fromNow()}</TableCell>

      <TableCell align="right">
        {isAssigned ? <CheckIcon fontSize="small" color="success" /> : <CloseIcon fontSize="small" color="error" />}
      </TableCell>
    </TableRow>
  );
};

type ProjectsProps = {
  action: ActionType;
  project: Project;
  selected: readonly string[];
  setSelected: Dispatch<SetStateAction<readonly string[]>>;
  handleClick: (e: React.MouseEvent<unknown>, id: string) => void;
};

const Users = (props: ProjectsProps) => {
  const { project, action, selected, setSelected, handleClick } = props;

  const paginate = usePagination({ defaultSort: "email" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const users = useConfgUsers(body);

  return (
    <Paper elevation={3}>
      <TableContainer sx={{ maxHeight: 630 }}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />

              {conf.COLUMNS.USERS.MGMT.map((column) => (
                <TableCell key={column.label} sortDirection={order} align={column.isHeader ? "left" : "right"}>
                  <TableSortLabel
                    active={sort === column.key}
                    disabled={column.disabled}
                    direction={sort === column.key ? order : "asc"}
                    onClick={(event: React.MouseEvent<unknown>) => handleSort(event, column.key)}
                  >
                    <b>{column.label}</b>
                    {sort === column.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {users.isLoading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={8}>
                  <LinearIndeterminate />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {users?.data?.data.length === 0 ? (
                <EmptyTableRow type="users" colSpan={8} />
              ) : (
                users?.data?.data?.map((user: User) => {
                  const isItemSelected = selected.includes(user.id);

                  return (
                    <UserRow
                      key={user.id}
                      user={user}
                      action={action}
                      project={project}
                      handleClick={handleClick}
                      isItemSelected={isItemSelected}
                    />
                  );
                })
              )}
            </TableBody>
          )}
        </Table>

        <TablePagination
          component="div"
          page={page}
          count={users?.data?.meta.total || 0}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onRowsPerPageChange={handleChangeLimit}
        />
      </TableContainer>
    </Paper>
  );
};

type ManageViewProps = {
  open: boolean;
  onClose: () => void;
  project: Project;
};

const ManageView = (props: ManageViewProps) => {
  const { open, project, onClose } = props;

  const auth = useAuth();
  const isKeyAdmin = ["org.key.custodian", "org.project.manager"].includes(auth.user.activeOrganization.role);

  const [action, setAction] = useState<ActionType>("assign");
  const [comment, setComment] = useState<string>("");
  const [selected, setSelected] = useState<readonly string[]>([]);

  const assign = useProxyProjectsAssign();
  const unassign = useProxyProjectsUnassign();

  const handleClick = (e: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleSubmit = () => {
    if (action === "assign") {
      assign.mutate({ projectId: project.id, userIds: selected, comment });
    } else if (action === "unassign") {
      unassign.mutate({ projectId: project.id, userIds: selected, comment });
    }
  };

  useEffect(() => {
    setSelected([]);
  }, [action]);

  useEffect(() => {
    if (assign.isSuccess) {
      setComment("");
      setSelected([]);
    }
  }, [assign.isSuccess]);

  useEffect(() => {
    if (unassign.isSuccess) {
      setComment("");
      setSelected([]);
    }
  }, [unassign.isSuccess]);

  return (
    <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <DialogTitle>
          <Chip color="success" icon={<AssignmentIndIcon />} label={<b>{project.name}</b>} />
        </DialogTitle>

        <Button size="small" color="inherit" onClick={onClose}>
          <CloseIcon />
        </Button>
      </Stack>

      <Divider />

      <DialogContent>
        {isKeyAdmin && (
          <Fragment>
            <Typography gutterBottom variant="h6" component="div">
              Users
            </Typography>

            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
              <DialogContentText variant="caption">
                To manage project please chose action type (right), then select users below.
              </DialogContentText>

              <EditAssignment action={action} setAction={setAction} />
            </Stack>

            <Users
              action={action}
              project={project}
              selected={selected}
              setSelected={setSelected}
              handleClick={handleClick}
            />

            <TextField
              fullWidth
              id="comment"
              type="comment"
              label="Note (optional)"
              margin="dense"
              variant="standard"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />

            <DialogActions>
              <Button type="submit" variant="contained" onClick={handleSubmit} disabled={selected.length === 0}>
                {assign.isPending || unassign.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Submit"}
              </Button>
            </DialogActions>
          </Fragment>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Manage = ({
  project,
  size = "medium",
  disabled = false,
  fullWidth = false
}: {
  size?: ButtonOwnProps["size"];
  detail?: any;
  project: Project;
  disabled?: boolean;
  fullWidth?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const Manage = (
    <Button size={size} type="submit" variant="contained" onClick={handleOpen} disabled={disabled} fullWidth={fullWidth}>
      Manage
    </Button>
  );

  return (
    <div>
      {project.activeUsers > 0 ? (
        <Tooltip title={`Click to manage project`} placement="left">
          {Manage}
        </Tooltip>
      ) : (
        Manage
      )}

      <ManageView open={open} project={project} onClose={handleClose} />
    </div>
  );
};

export default Manage;
